import { StanButton, StanCalendar, StanInput, StanModal } from '@/components';
import { StanFloatingMenu } from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { format } from 'date-fns';
import { FiChevronDown } from 'react-icons/fi';
import { useBlockTimeModal } from '../hooks/useBlockTimeModal';
import { TIME_OPTIONS } from '@/lib/utils/timeUtils';
import CalendarIconSvg from '@/assets/icons/ic-calendar.svg';
import ClockIconSvg from '@/assets/icons/ic-clock.svg';
import { useIsMobile } from '@/hooks/useMediaQuery';
interface BlockTimeModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedDate: Date;
    onSave: (blockTime: {
        id?: string;
        startDate: string;
        startTime: string;
        endDate: string;
        endTime: string;
        silent?: boolean;
    }) => Promise<void>;
    blockedTimeId?: string;
    initialStartTime?: string;
    initialEndTime?: string;
}

export function BlockTimeModal({
    isOpen,
    onClose,
    selectedDate,
    onSave,
    blockedTimeId,
    initialStartTime,
    initialEndTime,
}: BlockTimeModalProps) {
    const isMobile = useIsMobile();
    const {
        startDate,
        endDate,
        isEditMode,
        currentStartTime,
        currentEndTime,
        filteredEndTimeOptions,
        isInvalid,
        handleStartDateSelect,
        handleEndDateSelect,
        handleStartTimeSelect,
        handleEndTimeSelect,
        handleSave,
    } = useBlockTimeModal({
        isOpen,
        selectedDate,
        blockedTimeId,
        initialStartTime,
        initialEndTime,
        onSave,
        onClose,
    });

    const formattedStartDate = startDate ? format(startDate, 'MMM dd, yyyy') : 'Select Date';
    const formattedEndDate = endDate ? format(endDate, 'MMM dd, yyyy') : 'Select Date';

    return (
        <StanModal
            id='block-time-modal'
            isOpen={isOpen}
            onClose={onClose}
            title={isEditMode ? 'Edit Block Time' : 'Block Time'}
            size='md'
            hideCloseButton
        >
            <div className='-mx-1 space-y-4 overflow-x-hidden px-1'>
                <p className='text-gray text-body-sm text-center'>
                    Blocking time will result in your calendar being unavailable to book at that specific time.
                </p>

                <div className='grid grid-cols-2 gap-1.5'>
                    <div className='space-y-2'>
                        <label className='text-dark text-body-sm block font-medium'>From</label>
                        <StanFloatingMenu
                            id='start-date-picker'
                            placement='right'
                            offset={isMobile ? -100 : 0}
                            transitionOptions={{ duration: 0 }}
                            trigger={
                                <div className='floating-trigger-no-gap relative'>
                                    <StanInput
                                        id='start-date-picker'
                                        name='start-date-picker'
                                        value={formattedStartDate}
                                        onChange={() => {}}
                                        readOnly
                                        placeholder='Select Date'
                                        className='cursor-pointer text-sm'
                                        startIcon={CalendarIconSvg}
                                    />
                                    <FiChevronDown className='text-gray pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2' />
                                </div>
                            }
                        >
                            {({ closeMenu }) => (
                                <div className='w-auto'>
                                    <StanCalendar
                                        analyticsName='block-time-start-date'
                                        mode='single'
                                        selected={startDate}
                                        defaultMonth={startDate}
                                        onSelect={date => {
                                            handleStartDateSelect(date);
                                            closeMenu();
                                        }}
                                        disabled={{ before: new Date() }}
                                    />
                                </div>
                            )}
                        </StanFloatingMenu>
                    </div>

                    <div className='space-y-2'>
                        <label className='text-dark text-body-sm block font-medium'>To</label>
                        <StanFloatingMenu
                            id='end-date-picker'
                            placement='right'
                            offset={isMobile ? -100 : 0}
                            transitionOptions={{ duration: 0 }}
                            trigger={
                                <div className='floating-trigger-no-gap relative'>
                                    <StanInput
                                        id='end-date-picker'
                                        name='end-date-picker'
                                        value={formattedEndDate}
                                        onChange={() => {}}
                                        readOnly
                                        placeholder='Select Date'
                                        className='cursor-pointer text-sm'
                                        startIcon={CalendarIconSvg}
                                    />
                                    <FiChevronDown className='text-gray pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2' />
                                </div>
                            }
                        >
                            {({ closeMenu }) => (
                                <div className='w-auto'>
                                    <StanCalendar
                                        analyticsName='block-time-end-date'
                                        mode='single'
                                        selected={endDate}
                                        defaultMonth={endDate}
                                        onSelect={date => {
                                            handleEndDateSelect(date);
                                            closeMenu();
                                        }}
                                        disabled={{ before: startDate || new Date() }}
                                    />
                                </div>
                            )}
                        </StanFloatingMenu>
                    </div>
                </div>

                <div className='space-y-2'>
                    <div className='grid grid-cols-2 gap-1.5'>
                        <div className='space-y-2'>
                            <StanFloatingMenu
                                id='start-time-picker'
                                matchTriggerWidth
                                placement='right'
                                trigger={
                                    <div className='floating-trigger-no-gap relative'>
                                        <StanInput
                                            id='start-time-picker'
                                            name='start-time-picker'
                                            value={currentStartTime?.label || 'Select Time'}
                                            onChange={() => {}}
                                            readOnly
                                            placeholder='Select Time'
                                            className='cursor-pointer text-sm'
                                            startIcon={ClockIconSvg}
                                        />
                                        <FiChevronDown className='text-gray pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2' />
                                    </div>
                                }
                            >
                                {({ closeMenu }) => (
                                    <div className='max-h-60 overflow-y-auto'>
                                        {TIME_OPTIONS.map(option => (
                                            <button
                                                key={option.value}
                                                type='button'
                                                className='hover:bg-neutral-subtle w-full px-4 py-2 text-left text-sm transition-colors'
                                                onClick={() => {
                                                    handleStartTimeSelect(option);
                                                    closeMenu();
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </StanFloatingMenu>
                        </div>
                        <div className='space-y-2'>
                            <StanFloatingMenu
                                id='end-time-picker'
                                matchTriggerWidth
                                placement='right'
                                trigger={
                                    <div className='floating-trigger-no-gap relative'>
                                        <StanInput
                                            id='end-time-picker'
                                            name='end-time-picker'
                                            value={currentEndTime?.label || 'Select Time'}
                                            onChange={() => {}}
                                            readOnly
                                            placeholder='Select Time'
                                            className='cursor-pointer text-sm'
                                            startIcon={ClockIconSvg}
                                            error={isInvalid}
                                        />
                                        <FiChevronDown className='text-gray pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2' />
                                    </div>
                                }
                            >
                                {({ closeMenu }) => (
                                    <div className='max-h-60 overflow-y-auto'>
                                        {filteredEndTimeOptions.map(option => (
                                            <button
                                                key={option.value}
                                                type='button'
                                                className='hover:bg-neutral-subtle w-full px-4 py-2 text-left text-sm transition-colors'
                                                onClick={() => {
                                                    handleEndTimeSelect(option);
                                                    closeMenu();
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </StanFloatingMenu>
                        </div>
                    </div>
                    {isInvalid && (
                        <p className='text-negative-active text-label-xs-bold mt-1'>
                            End time must be after start time
                        </p>
                    )}
                </div>

                <div className='flex gap-3 pt-4'>
                    <StanButton name='cancel-block-time' onClick={onClose} color='secondary' className='flex-1'>
                        Cancel
                    </StanButton>
                    <StanButton
                        name='save-block-time'
                        onClick={handleSave}
                        color='primary'
                        className='flex-1'
                        disabled={isInvalid}
                    >
                        Save
                    </StanButton>
                </div>
            </div>
        </StanModal>
    );
}
